import { stripTrailingSlash } from '@/lib/api/buildApiUrl';
import HttpMethod from '@/lib/constants/HttpMethod.enum';
import getGoogleAccessToken from '../../utils/google/getGoogleAccessToken';

const GOOGLE_APIS_BASE_URL = 'https://www.googleapis.com';
const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;

class GoogleDriveApiClient {
  private readonly apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = stripTrailingSlash(GOOGLE_APIS_BASE_URL);
  }

  get<T>(url: string): Promise<T> {
    return this.performApiRequest<T>(url, HttpMethod.GET);
  }

  post<T>(url: string, body: FormData | string): Promise<T> {
    return this.performApiRequest<T>(url, HttpMethod.POST, body);
  }

  async fetchRawApiResponse(
    url: string,
    fetchOptions: RequestInit,
  ): Promise<Response> {
    const accessToken = await getGoogleAccessToken();

    return fetch(this.getFullUrl(url), {
      ...fetchOptions,
      headers: {
        ...fetchOptions.headers,
        Authorization: `Bearer ${accessToken.getValue()}`,
      },
    });
  }

  private async performApiRequest<T>(
    url: string,
    method: HttpMethod,
    body?: FormData | string,
  ): Promise<T> {
    const accessToken = await getGoogleAccessToken();

    const response = await fetch(this.getFullUrl(url), {
      method,
      headers: {
        Authorization: `Bearer ${accessToken.getValue()}`,
      },
      body,
    });

    const responseContent = await response.json();

    return responseContent as T;
  }

  private getFullUrl(path: string): string {
    const url = this.getBaseUrl(path);
    const apiKeyQueryParam = `key=${GOOGLE_API_KEY}`;

    if (url.includes('?')) {
      return `${url}&${apiKeyQueryParam}`;
    }

    return `${url}?${apiKeyQueryParam}`;
  }

  private getBaseUrl(path: string): string {
    if (path.startsWith('https://')) {
      return path;
    }

    if (path.startsWith('/')) {
      return `${this.apiBaseUrl}${path}`;
    }

    return `${this.apiBaseUrl}/${path}`;
  }
}

const googleDriveApiClient = new GoogleDriveApiClient();

export default googleDriveApiClient;
